import Connections from '../../connections';
import {
  action, observable,
} from 'mobx';
import {
  createOffer,
  getOfferById,
  getOffers,
  sortOrder,
  updateOffer,
} from "../../axios/offersRequests";
import { DefaultLanguage } from "../../constants/languages";
import { getHeader } from "../../helperFunctions/getHeader";
import { uploadSingleImage } from "../../helperFunctions/uploadService";
import { isImageArray } from "../storeInterfaces/featuredArticlesStore";
import { convertToRaw } from "draft-js";
import { convertCountriesToISOCodeString } from "constants/countries";
import { stores } from "mobx-store";
import { ICountry } from "mobx-store/storeInterfaces/authInterface";

export class OffersStore {
  @observable offersList: any[] = [];

  @observable offersCount: number = 0;

  @observable selectedOffer = {};

  @observable loading = false;

  @action
  public async getOffers(
    countries: ICountry[],
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.loading = true;
      const availableCountries = convertCountriesToISOCodeString(countries);
      const params = {
        availableCountries,
      };

      const config = {
        ...getHeader(langCode),
        params,
      };

      const response = await Connections.wadiDeglaAxios.httpGet(
        getOffers,
        config
      );
      const { offers, count } = response.data;
      this.offersList = this.restructureOffersList(offers);
      this.offersCount = count;
    } catch (error) {
      this.loading = false;
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async getOfferById(
    id: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpGet(
        getOfferById(id),
        getHeader(langCode)
      );
      this.selectedOffer = this.restructureOffer(response.data.offer);
    } catch (error) {
      this.loading = false;
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async updateOffersSort(data: any): Promise<void> {
    try {
      this.loading = true;
      const response = await Connections.wadiDeglaAxios.httpPatch(
        sortOrder,
        data
      );
    } catch (error) {
      this.loading = false;
      return Promise.reject(error);
    } finally {
      this.loading = false;
    }
  }

  @action.bound
  public async createOffer(
    newOffer: any,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    return new Promise<void>(async (res, rej) => {
      try {
        this.loading = true;
        if (isImageArray(newOffer.imageURL)) {
          newOffer.imageURL = await uploadSingleImage(newOffer.imageURL);
        }
        if (isImageArray(newOffer.iconURL)) {
          newOffer.iconURL = await uploadSingleImage(newOffer.iconURL);
        }
        newOffer.steps = JSON.stringify(
          convertToRaw(newOffer.steps.getCurrentContent())
        );
        newOffer.description = JSON.stringify(
          convertToRaw(newOffer.description.getCurrentContent())
        );

        newOffer.expirationDate = new Date(newOffer.expirationDate).getTime();
        const response = await Connections.wadiDeglaAxios.httpPost(
          createOffer,
          newOffer,
          getHeader(langCode)
        );
        res(response.data.offerId);
      } catch (error) {
        this.loading = false;
        rej(error);
      }
    });
  }

  @action.bound
  public async updateOffer(
    updatedOffer: any,
    offerId: number,
    langCode: string = DefaultLanguage
  ): Promise<void> {
    try {
      if (isImageArray(updatedOffer.imageURL)) {
        updatedOffer.imageURL = await uploadSingleImage(updatedOffer.imageURL);
      }
      if (isImageArray(updatedOffer.iconURL)) {
        updatedOffer.iconURL = await uploadSingleImage(updatedOffer.iconURL);
      }
      if (updatedOffer.steps) {
        updatedOffer.steps = JSON.stringify(
          convertToRaw(updatedOffer.steps.getCurrentContent())
        );
      }
      if (updatedOffer.description) {
        updatedOffer.description = JSON.stringify(
          convertToRaw(updatedOffer.description.getCurrentContent())
        );
      }

      if (updatedOffer.expirationDate) {
        updatedOffer.expirationDate = new Date(
          updatedOffer.expirationDate
        ).getTime();
      }
      await Connections.wadiDeglaAxios.httpPut(
        updateOffer(offerId),
        updatedOffer,
        getHeader(langCode)
      );
    } catch (error) {
      return Promise.reject(error);
    }
  }

  private restructureOffersList(offers: any[]): any[] {
    const OffersList = [];
    for (const offer of offers) {
      OffersList.push({
        offerId: offer.offerId,
        title: offer.title,
        expirationDate: offer.expirationDate,
        description: offer.description,
        terms: offer.terms,
        steps: offer.steps,
        imageURL: offer.imageURL,
        isActive: offer.isActive,
        isHotDeal: offer.isHotDeal,
        discountPercentage: offer.discountPercentage,
        sortOrder: offer.sortOrder,
      });
    }
    return OffersList;
  }

  private restructureOffer(offer: any): any {
    const restructuredOffer = {
      offerId: offer.offerId,
      title: offer.title,
      expirationDate: offer.expirationDate
        ? new Date(offer.expirationDate)
        : null,
      description: offer.steps ? JSON.parse(offer.description) : "",
      terms: offer.terms,
      steps: offer.steps ? JSON.parse(offer.steps) : "",
      imageURL: offer.imageURL
        ? [
            {
              imageUrl: offer.imageURL,
              key: 1,
              type: "image",
            },
          ]
        : [],
      iconURL: offer.iconURL
        ? [
            {
              imageUrl: offer.iconURL,
              key: 1,
              type: "image",
            },
          ]
        : [],
      isActive: offer.isActive,
      isHotDeal: offer.isHotDeal,
      discountPercentage: offer.discountPercentage,
      navigationType: offer.navigationType,
      navigationURI: offer.navigationURI,
      navigationEntityParams: offer.navigationEntityParams,
      navigationEntityId: offer.navigationEntityId,
      nativeModuleName: offer.nativeModuleName,
      nativeModuleMethodName: offer.nativeModuleMethodName,
    };
    return restructuredOffer;
  }
}
